import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Layers, ChevronDown, ChevronRight, Server, Package, Boxes } from "lucide-react";

const METHOD_TONE = {
  GET:    "text-emerald-700 bg-emerald-50 border-emerald-200",
  POST:   "text-sky-700 bg-sky-50 border-sky-200",
  PUT:    "text-amber-700 bg-amber-50 border-amber-200",
  DELETE: "text-rose-700 bg-rose-50 border-rose-200",
  PATCH:  "text-violet-700 bg-violet-50 border-violet-200",
};

export default function AdminRoutesMap() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/admin/routes-map");
        setData(r.data);
        // Auto-expand the monolith so the biggest burndown surface is visible first.
        setExpanded({ "server (monolith)": true });
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load routes map");
      } finally { setLoading(false); }
    })();
  }, []);

  const toggle = (name) => setExpanded((e) => ({ ...e, [name]: !e[name] }));

  const filtered = (endpoints) => {
    if (!q.trim()) return endpoints;
    const s = q.trim().toLowerCase();
    return endpoints.filter((r) => r.path.toLowerCase().includes(s) || r.method.toLowerCase().includes(s));
  };

  if (loading) return (
    <div className="min-h-screen p-10 flex items-center gap-2 text-slate-500" data-testid="routes-map-loading">
      <Loader2 className="w-4 h-4 animate-spin" />Loading routes map…
    </div>
  );
  if (!data) return null;

  const bd = data.burndown;
  const pct = bd.percent_extracted;

  return (
    <div className="min-h-screen p-6 sm:p-10">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <div className="w-12 h-12 rounded-2xl bg-indigo-100 text-indigo-600 flex items-center justify-center">
            <Layers className="w-6 h-6" />
          </div>
          <div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-indigo-600">// ARCHITECTURE / REFACTOR BURNDOWN</div>
            <h1 className="font-display font-black text-3xl sm:text-4xl">Routes Map</h1>
            <p className="text-sm text-slate-500 mt-1 max-w-2xl">
              Every endpoint on the FastAPI app, grouped by the Python module that owns it. Watch the <code className="text-xs bg-slate-100 px-1 rounded">server (monolith)</code> bar shrink as the last endpoints migrate into <code className="text-xs bg-slate-100 px-1 rounded">/routes/*</code>.
            </p>
          </div>
        </div>

        {/* Burndown scoreboard */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8" data-testid="routes-map-burndown">
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">TOTAL ENDPOINTS</div>
            <div className="font-display font-black text-4xl mt-1" data-testid="routes-map-total">{data.total_endpoints}</div>
            <div className="text-xs text-slate-500 mt-1">across {data.modules.length} module{data.modules.length !== 1 ? "s" : ""}</div>
          </div>
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
            <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-700">EXTRACTED</div>
            <div className="font-display font-black text-4xl mt-1 text-emerald-700" data-testid="routes-map-extracted">{bd.extracted}</div>
            <div className="text-xs text-emerald-700 mt-1">{pct}% domain-isolated</div>
          </div>
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
            <div className="text-[10px] font-mono uppercase tracking-widest text-amber-700">STILL IN MONOLITH</div>
            <div className="font-display font-black text-4xl mt-1 text-amber-700" data-testid="routes-map-monolith">{bd.monolith_remaining}</div>
            <div className="text-xs text-amber-700 mt-1">target zero</div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mb-8">
          <div className="flex justify-between text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1.5">
            <span>REFACTOR PROGRESS</span>
            <span className="text-emerald-600">{pct}%</span>
          </div>
          <div className="h-3 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all duration-700" style={{ width: `${pct}%` }} data-testid="routes-map-progress-bar" />
          </div>
        </div>

        {/* Search */}
        <div className="mb-4">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filter endpoints (path or method)…"
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
            data-testid="routes-map-search"
          />
        </div>

        {/* Modules */}
        <div className="space-y-3" data-testid="routes-map-modules">
          {data.modules.map((m) => {
            const visible = filtered(m.endpoints);
            if (q.trim() && visible.length === 0) return null;
            const isOpen = !!expanded[m.name];
            const Icon = m.is_monolith ? Server : (m.name.startsWith("services/") ? Boxes : Package);
            return (
              <div key={m.name} className={`rounded-2xl border ${m.is_monolith ? "border-amber-200 bg-amber-50/30" : "border-slate-200 bg-white"}`} data-testid={`routes-map-module-${m.name.replace(/[^a-z0-9]+/gi, "-")}`}>
                <button
                  onClick={() => toggle(m.name)}
                  className="w-full flex items-center justify-between p-4 hover:bg-slate-50/70 rounded-2xl"
                >
                  <div className="flex items-center gap-3">
                    {isOpen ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center border ${m.is_monolith ? "border-amber-300 bg-amber-100 text-amber-700" : "border-indigo-200 bg-indigo-50 text-indigo-700"}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="text-left">
                      <div className="font-display font-semibold text-base flex items-center gap-2">
                        {m.name}
                        {m.is_monolith && <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300">MONOLITH</span>}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">{m.count} endpoint{m.count !== 1 ? "s" : ""}</div>
                    </div>
                  </div>
                  <div className={`font-display font-black text-2xl ${m.is_monolith ? "text-amber-700" : "text-slate-700"}`}>{m.count}</div>
                </button>
                {isOpen && (
                  <div className="border-t border-slate-100 p-4 space-y-1" data-testid={`routes-map-endpoints-${m.name.replace(/[^a-z0-9]+/gi, "-")}`}>
                    {visible.map((r, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs font-mono">
                        <span className={`inline-flex items-center justify-center w-14 py-0.5 rounded border ${METHOD_TONE[r.method] || "border-slate-200 bg-slate-50 text-slate-700"}`}>{r.method}</span>
                        <span className="text-slate-800 truncate">{r.path}</span>
                        <span className="text-slate-400 ml-auto">{r.name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
